import { useRef, useState, type ReactNode } from 'react';

import { calendarDay } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { GROUP_LABEL } from '../../shared/ui/group-label';
import { Segmented } from '../../shared/ui/segmented';
import {
  RETENTION_PERIODS,
  periodLabel,
  type TRetentionPeriod,
  type TStorageLevels,
} from '../../entities/retention';
import { ImpactDialog } from './impact-dialog';

const SEGMENTS = RETENTION_PERIODS.map((period) => ({ value: period, label: periodLabel(period) }));

const Level = ({
  n,
  name,
  lead,
  children,
}: {
  n: number;
  name: string;
  lead: string;
  children: ReactNode;
}) => (
  <div className="flex-1 bg-field-muted px-4 py-input">
    <span className={GROUP_LABEL}>
      {n} · {name}
    </span>
    <p className="mt-label text-data text-primary">{lead}</p>
    {children}
  </div>
);

export const LevelCards = ({ levels, now }: { levels: TStorageLevels; now: number }) => {
  const [choosing, setChoosing] = useState<TRetentionPeriod | null>(null);
  const segmented = useRef<HTMLDivElement>(null);
  const fromOff = levels.defaultPeriod === 'forever' && levels.foreverSince !== null;

  return (
    <div className="flex flex-col gap-4 px-card pb-4">
      {fromOff && (
        <div className="rounded-panel border border-border-sunken p-4">
          <p className="text-data font-semibold text-primary">Everything here keeps Forever</p>
          <p className="mt-1 text-meta leading-18 text-muted">
            Automatic removal was off in this organization. When retention moved to folders and
            flows on {calendarDay(levels.foreverSince!, now)}, the default was set to Forever so
            nothing changed. Nothing has been deleted.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => segmented.current?.querySelector('button')?.focus()}
            >
              Choose a default
            </Button>
            <span className="font-mono text-micro text-faint">or set it per folder</span>
          </div>
        </div>
      )}
      <div className="flex items-stretch gap-px overflow-hidden rounded-panel">
        <Level n={1} name="Organization" lead="Every folder and flow starts from this.">
          <div ref={segmented} className="mt-3">
            <Segmented
              aria-label="The organization default"
              segments={SEGMENTS}
              value={levels.defaultPeriod}
              onChange={(period) => period !== levels.defaultPeriod && setChoosing(period)}
            />
          </div>
          <p className="mt-2 text-meta leading-17 text-muted">
            Forever is how you turn cleanup off — there is no separate switch. Anything on Forever
            only grows.
          </p>
        </Level>
        <Level n={2} name="Folder" lead="A folder can set its own period.">
          <p className="mt-1 text-meta leading-17 text-muted">
            Flows inside, and subfolders, take it. A flow created in a folder starts with the
            folder’s period.
          </p>
        </Level>
        <Level n={3} name="Flow" lead="An exception, set on one flow.">
          <p className="mt-1 text-meta leading-17 text-muted">
            It stays when the folder changes. Here: {levels.exceptionCount.toLocaleString('en-US')}{' '}
            of {levels.flowCount.toLocaleString('en-US')} flows. Remove it and the flow follows its
            folder again.
          </p>
        </Level>
      </div>
      {choosing && (
        <ImpactDialog
          target={{ level: 'organization' }}
          period={choosing}
          from={levels.defaultPeriod}
          onClose={() => setChoosing(null)}
        />
      )}
    </div>
  );
};
